// src/components/admin/PortalPersonalisationEditor.jsx
// Communications > Portal Personalisation Campaigns
// Lets the admin set up personalised content in the Customer Portal for specific customer segments:
//   1) Pop up Personalisation   — a popup (image, header, body, CTA) shown to the segment
//   2) Content Personalisation  — hero / gallery images that take priority over the Images page
import React, { useState, useEffect, useCallback } from 'react';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import PersonalisationImageField from './PersonalisationImageField';
import PersonalisationScheduling, { initialSchedule, validateSchedule } from './PersonalisationScheduling';
import PersonalisationTargeting, { initialTargeting, validateTargeting } from './PersonalisationTargeting';
import { PortalPopupCard } from '../customer/PortalPopup';
import { LivePreviewModal, RecipientsModal } from './PersonalisationModals';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import {
  resolveAudience,
  createPersonalisationCampaign,
  listPersonalisationCampaigns,
  deletePersonalisationCampaign,
  getCampaignState
} from '../../api/portalPersonalisationService';
import '../../styles/portal-personalisation.css';

const IMAGE_PLACEHOLDER =
  'Drag & drop a hero image here, or click to select a file which can be in the following formats (JPG / PNG  / GIF /  URL).';
const HERO_MEDIA_PLACEHOLDER =
  'Drag & drop a hero image here, or click to select a file which can be in the following formats (JPG / PNG  / GIF /  MP4 video /  URL).';
const GALLERY_MEDIA_PLACEHOLDER =
  'Drag & drop a gallery image here, or click to select a file which can be in the following formats (JPG / PNG  / GIF /  MP4 video /  URL).';

const HEADER_MAX = 50;
const BODY_MAX = 150;
const CTA_MAX = 30;
const GALLERY_MAX = 5; // same limit as the Images page
const HERO_VIDEO_MAX_MB = 15;
const GALLERY_VIDEO_MAX_MB = 8;

const initialPopupForm = () => ({
  imageUrl: '',
  headerText: '',
  bodyText: '',
  ctaText: '',
  maxPerDay: 1,
  maxPerMonth: 5,
  ...initialSchedule(),
  ...initialTargeting()
});

const initialContentForm = () => ({
  heroImage: '',
  galleryImages: [],
  ...initialSchedule(),
  ...initialTargeting()
});

const SCHEDULE_KEYS = [
  'schedulingType', 'scheduledDate', 'scheduledTime', 'scheduledEndDate', 'scheduledEndTime', 'recurring'
];
const pick = (source, keys) => keys.reduce((acc, key) => ({ ...acc, [key]: source[key] }), {});

const formatDateTime = (date, time) => {
  if (!date) return '—';
  const d = new Date(`${date}T${time || '00:00'}`);
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}${time ? `, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''}`;
};

const describeSchedule = (c) => {
  if (c.schedulingType === 'recurring') {
    const r = c.recurring || {};
    const end = r.endType === 'date' ? `until ${formatDateTime(r.endDate)}` : `for ${r.numberOfCycles} cycles`;
    return `${r.frequency} from ${formatDateTime(r.startDate)}, ${c.scheduledTime}–${c.scheduledEndTime}, ${end}`;
  }
  return `${formatDateTime(c.scheduledDate, c.scheduledTime)} → ${formatDateTime(c.scheduledEndDate, c.scheduledEndTime)}`;
};

const STATE_LABELS = { live: 'Live', scheduled: 'Scheduled', ended: 'Ended', cancelled: 'Cancelled' };

function PortalPersonalisationEditor({ salonId, onUpdate }) {
  const { currentUser } = useSalonAuth();
  const [activeSubTab, setActiveSubTab] = useState('popup'); // popup | content | history

  const [popupForm, setPopupForm] = useState(initialPopupForm);
  const [contentForm, setContentForm] = useState(initialContentForm);
  const [errors, setErrors] = useState({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewCampaign, setPreviewCampaign] = useState(null);
  const [previewSalonImages, setPreviewSalonImages] = useState(null);
  const [recipientsCampaign, setRecipientsCampaign] = useState(null);

  const isPopup = activeSubTab === 'popup';
  const form = isPopup ? popupForm : contentForm;
  const setForm = isPopup ? setPopupForm : setContentForm;

  const updateForm = (patch) => {
    setForm(prev => ({ ...prev, ...patch }));
    setErrors({});
  };

  const loadCampaigns = useCallback(async () => {
    if (!salonId) return;
    setCampaignsLoading(true);
    try {
      setCampaigns(await listPersonalisationCampaigns(salonId));
    } catch (err) {
      console.error('Error loading personalisation campaigns:', err);
      setError('Failed to load existing personalisations: ' + err.message);
    } finally {
      setCampaignsLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    if (activeSubTab === 'history') loadCampaigns();
  }, [activeSubTab, loadCampaigns]);

  const switchSubTab = (tab) => {
    setActiveSubTab(tab);
    setErrors({});
    setError('');
    setSuccess('');
  };

  const validate = () => {
    const found = {};

    if (isPopup) {
      if (!popupForm.headerText.trim()) found.headerText = 'Header text is required';
      if (!popupForm.bodyText.trim()) found.bodyText = 'Body text is required';
      if (!popupForm.ctaText.trim()) found.ctaText = 'CTA text is required';
      const { maxPerDay, maxPerMonth } = popupForm;
      if (!Number.isInteger(maxPerDay) || maxPerDay < 1) {
        found.maxPerDay = 'Enter a whole number of at least 1';
      }
      if (!Number.isInteger(maxPerMonth) || maxPerMonth < 1) {
        found.maxPerMonth = 'Enter a whole number of at least 1';
      } else if (Number.isInteger(maxPerDay) && maxPerMonth < maxPerDay) {
        found.maxPerMonth = 'Per-month limit cannot be lower than the per-day limit';
      }
    } else if (!contentForm.heroImage && contentForm.galleryImages.length === 0) {
      found.images = 'Add a hero image or video, gallery items, or both';
    }

    Object.assign(found, validateSchedule(form), validateTargeting(form));
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!validate()) {
      setError('Please fix the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      const { audienceAll, customers } = await resolveAudience(salonId, form.targetingType, form.targetingCriteria);
      if (customers.length === 0) {
        throw new Error('No customers match the targeting criteria, so nobody would see this personalisation.');
      }

      const base = {
        salonId,
        type: isPopup ? 'popup' : 'content',
        ...pick(form, SCHEDULE_KEYS),
        recurring: form.schedulingType === 'recurring' ? form.recurring : null,
        // One-time campaigns don't use the recurring block; recurring ones don't use the fixed dates.
        ...(form.schedulingType === 'recurring'
          ? { scheduledDate: '', scheduledEndDate: '' }
          : {}),
        targetingType: form.targetingType,
        targetingCriteria: form.targetingCriteria,
        audienceAll,
        audienceCount: customers.length,
        createdBy: currentUser?.uid || '',
        createdByEmail: currentUser?.email || ''
      };

      const campaign = isPopup
        ? {
            ...base,
            name: `Pop up: ${popupForm.headerText.trim()}`,
            imageUrl: popupForm.imageUrl || '',
            headerText: popupForm.headerText.trim(),
            bodyText: popupForm.bodyText.trim(),
            ctaText: popupForm.ctaText.trim(),
            maxPerDay: popupForm.maxPerDay,
            maxPerMonth: popupForm.maxPerMonth
          }
        : {
            ...base,
            name: `Hero / Gallery images${form.schedulingType === 'one-time' ? ` from ${formatDateTime(form.scheduledDate)}` : ''}`,
            heroImage: contentForm.heroImage || '',
            galleryImages: contentForm.galleryImages
          };

      await createPersonalisationCampaign({ campaign, recipients: audienceAll ? [] : customers });

      setSuccess(
        `${isPopup ? 'Pop up' : 'Content'} personalisation scheduled — it will show to ${
          audienceAll ? 'all' : customers.length
        } customer${audienceAll || customers.length !== 1 ? 's' : ''} during the campaign dates.`
      );
      setTimeout(() => setSuccess(''), 6000);
      setForm(isPopup ? initialPopupForm() : initialContentForm());
      setErrors({});
      window.scrollTo(0, 0);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error saving portal personalisation:', err);
      setError('Failed to save personalisation: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(isPopup ? initialPopupForm() : initialContentForm());
    setErrors({});
    setError('');
    setSuccess('');
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Delete "${campaign.name}"? Customers will stop seeing it immediately. This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingId(campaign.id);
      await deletePersonalisationCampaign(campaign.id);
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
    } catch (err) {
      console.error('Error deleting personalisation:', err);
      setError('Failed to delete personalisation: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Live / scheduled campaigns are "existing"; ended / cancelled ones move to "past"
  const campaignsWithState = campaigns.map(campaign => ({ campaign, state: getCampaignState(campaign) }));
  const existingCampaigns = campaignsWithState.filter(({ state }) => state === 'live' || state === 'scheduled');
  const pastCampaigns = campaignsWithState.filter(({ state }) => state === 'ended' || state === 'cancelled');

  // The preview falls back to the Images-page hero/gallery for anything a campaign leaves unset
  const openLivePreview = async (campaign) => {
    setPreviewSalonImages(null);
    setPreviewCampaign(campaign);
    if (campaign.type !== 'content') return;
    try {
      const snap = await getDoc(doc(db, 'salons', salonId));
      setPreviewSalonImages(snap.exists() ? snap.data().branding?.images || {} : {});
    } catch (err) {
      console.error('Error loading Images page content for preview:', err);
      setPreviewSalonImages({});
    }
  };

  const renderCampaignList = (items, emptyText) => {
    if (items.length === 0) return <p className="form-helper-text">{emptyText}</p>;
    return (
      <div className="personalisation-list">
        {items.map(({ campaign, state }) => (
          <div key={campaign.id} className="personalisation-list-item">
            <div className="personalisation-list-main">
              <div className="personalisation-list-title">
                {campaign.name}
                <span className={`personalisation-state personalisation-state-${state}`}>
                  {STATE_LABELS[state]}
                </span>
              </div>
              <div className="personalisation-list-meta">
                {campaign.type === 'popup' ? 'Pop up' : 'Hero / Gallery content'} · {describeSchedule(campaign)}
              </div>
              {campaign.type === 'popup' && (
                <div className="personalisation-list-meta">
                  Shown at most {campaign.maxPerDay || 1}× per day
                  {campaign.maxPerMonth ? `, ${campaign.maxPerMonth}× per month` : ''} per customer
                </div>
              )}
              <div className="personalisation-list-meta">
                Audience: {campaign.audienceAll
                  ? 'All customers'
                  : `${campaign.audienceCount ?? 0} customer${campaign.audienceCount === 1 ? '' : 's'} (${(campaign.targetingType || '').replace(/_/g, ' ')})`}
              </div>
            </div>
            <div className="personalisation-list-actions">
              {state === 'live' && (
                <button type="button" className="preview-button" onClick={() => openLivePreview(campaign)}>
                  Live Preview
                </button>
              )}
              <button type="button" className="save-criteria-button" onClick={() => setRecipientsCampaign(campaign)}>
                View Recipients
              </button>
              <button
                type="button"
                className="delete-confirm-button"
                onClick={() => handleDelete(campaign)}
                disabled={deletingId === campaign.id}
              >
                {deletingId === campaign.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCounter = (text, max) => (
    <div className={`form-helper-text char-counter ${text.length >= max ? 'at-limit' : ''}`}>
      {text.length} / {max} characters
    </div>
  );

  return (
    <div className="notifications-editor portal-personalisation-editor">
      <h2>Portal Personalisation Campaigns</h2>

      <div className="sub-tab-navigation">
        <button
          type="button"
          className={`sub-tab-button ${activeSubTab === 'popup' ? 'active' : ''}`}
          onClick={() => switchSubTab('popup')}
        >
          Pop up Personalisation
        </button>
        <button
          type="button"
          className={`sub-tab-button ${activeSubTab === 'content' ? 'active' : ''}`}
          onClick={() => switchSubTab('content')}
        >
          Content Personalisation
        </button>
        <button
          type="button"
          className={`sub-tab-button ${activeSubTab === 'history' ? 'active' : ''}`}
          onClick={() => switchSubTab('history')}
        >
          Personalisation History
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {previewCampaign && (previewCampaign.type !== 'content' || previewSalonImages) && (
        <LivePreviewModal
          campaign={previewCampaign}
          salonImages={previewSalonImages}
          onClose={() => setPreviewCampaign(null)}
        />
      )}
      {recipientsCampaign && (
        <RecipientsModal campaign={recipientsCampaign} onClose={() => setRecipientsCampaign(null)} />
      )}

      {activeSubTab === 'history' && (
        <>
          <div className="notification-form">
            <h3>Existing Personalisations</h3>
            {campaignsLoading ? (
              <p>Loading...</p>
            ) : (
              renderCampaignList(
                existingCampaigns,
                'No live or scheduled personalisations. Create one from the Pop up Personalisation or Content Personalisation pill.'
              )
            )}
          </div>

          <div className="notification-form">
            <h3>Past Personalisations</h3>
            {campaignsLoading ? (
              <p>Loading...</p>
            ) : (
              renderCampaignList(pastCampaigns, 'No past personalisations yet.')
            )}
          </div>
        </>
      )}

      {activeSubTab !== 'history' && (
        <div className="notification-form">
          <h3>{isPopup ? 'Create Pop up Personalisation' : 'Create Content Personalisation'}</h3>

          {isPopup ? (
            <>
            <div className="form-section">
              <h4>Pop up Content</h4>

              <PersonalisationImageField
                id="personalisation-popup-image"
                label="Image"
                salonId={salonId}
                folder="popup"
                value={popupForm.imageUrl}
                onChange={(imageUrl) => updateForm({ imageUrl })}
                placeholder={IMAGE_PLACEHOLDER}
              />

              <div className="form-group">
                <label htmlFor="popupHeaderText">
                  Header Text<span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="popupHeaderText"
                  value={popupForm.headerText}
                  onChange={(e) => updateForm({ headerText: e.target.value })}
                  maxLength={HEADER_MAX}
                  className={errors.headerText ? 'input-error' : ''}
                />
                {errors.headerText && <span className="field-error-message">{errors.headerText}</span>}
                {renderCounter(popupForm.headerText, HEADER_MAX)}
              </div>

              <div className="form-group">
                <label htmlFor="popupBodyText">
                  Body Text<span className="required-asterisk">*</span>
                </label>
                <textarea
                  id="popupBodyText"
                  rows="4"
                  value={popupForm.bodyText}
                  onChange={(e) => updateForm({ bodyText: e.target.value })}
                  maxLength={BODY_MAX}
                  className={errors.bodyText ? 'input-error' : ''}
                />
                {errors.bodyText && <span className="field-error-message">{errors.bodyText}</span>}
                {renderCounter(popupForm.bodyText, BODY_MAX)}
              </div>

              <div className="form-group">
                <label htmlFor="popupCtaText">
                  CTA Text<span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="popupCtaText"
                  value={popupForm.ctaText}
                  onChange={(e) => updateForm({ ctaText: e.target.value })}
                  maxLength={CTA_MAX}
                  className={errors.ctaText ? 'input-error' : ''}
                />
                {errors.ctaText && <span className="field-error-message">{errors.ctaText}</span>}
                {renderCounter(popupForm.ctaText, CTA_MAX)}
                <div className="form-helper-text">
                  The button takes the customer to the booking page.
                </div>
              </div>

              <div className="personalisation-popup-preview">
                <h5>Pop up Preview</h5>
                <div className="personalisation-popup-preview-stage">
                  <PortalPopupCard
                    imageUrl={popupForm.imageUrl}
                    headerText={popupForm.headerText}
                    bodyText={popupForm.bodyText}
                    ctaText={popupForm.ctaText}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Pop up Frequency</h4>
              <p className="form-description">
                How many times the pop up can appear for each targeted customer. Each time it is shown to a
                customer counts once; days and months follow the customer's own calendar.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="popupMaxPerDay">
                    Times per day<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="popupMaxPerDay"
                    min="1"
                    step="1"
                    value={Number.isNaN(popupForm.maxPerDay) ? '' : popupForm.maxPerDay}
                    onChange={(e) => updateForm({ maxPerDay: parseInt(e.target.value, 10) })}
                    className={errors.maxPerDay ? 'input-error' : ''}
                  />
                  {errors.maxPerDay && <span className="field-error-message">{errors.maxPerDay}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="popupMaxPerMonth">
                    Times per month<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="popupMaxPerMonth"
                    min="1"
                    step="1"
                    value={Number.isNaN(popupForm.maxPerMonth) ? '' : popupForm.maxPerMonth}
                    onChange={(e) => updateForm({ maxPerMonth: parseInt(e.target.value, 10) })}
                    className={errors.maxPerMonth ? 'input-error' : ''}
                  />
                  {errors.maxPerMonth && <span className="field-error-message">{errors.maxPerMonth}</span>}
                </div>
              </div>
              <div className="form-helper-text">
                Each time the customer opens the home page (for example after logging in) the pop up can show once,
                until a limit is reached. Both limits apply: it stops as soon as either one is hit.
              </div>
            </div>
            </>
          ) : (
            <div className="form-section">
              <h4>Hero and Gallery Images</h4>
              <p className="form-description">
                These images are shown to the targeted customers during the campaign dates instead of the
                Hero and Gallery images set on the Images page. Anything left empty here falls back to the Images page.
              </p>
              {errors.images && <div className="error-message">{errors.images}</div>}

              <PersonalisationImageField
                id="personalisation-hero-image"
                label="Hero Image"
                salonId={salonId}
                folder="hero"
                value={contentForm.heroImage}
                onChange={(heroImage) => updateForm({ heroImage })}
                placeholder={HERO_MEDIA_PLACEHOLDER}
                helperText={`Image, or an MP4 video (H.264 / AAC, up to ${HERO_VIDEO_MAX_MB}MB). A hero video plays automatically on a loop, with its sound where the browser allows it (otherwise from the customer's first tap), and is muted whenever the customer plays a gallery video.`}
                allowVideo
                maxVideoMb={HERO_VIDEO_MAX_MB}
              />

              <PersonalisationImageField
                id="personalisation-gallery-image"
                label="Gallery Image"
                helperText={`Up to ${GALLERY_MAX} items, images or MP4 videos (H.264 / AAC, up to ${GALLERY_VIDEO_MAX_MB}MB each). Videos don't autoplay: the customer taps to play and hears the sound, and any hero video is muted while it plays.`}
                salonId={salonId}
                folder="gallery"
                value={contentForm.galleryImages}
                onChange={(galleryImages) => updateForm({ galleryImages })}
                placeholder={GALLERY_MEDIA_PLACEHOLDER}
                multiple
                maxFiles={GALLERY_MAX}
                allowVideo
                maxVideoMb={GALLERY_VIDEO_MAX_MB}
              />
            </div>
          )}

          <PersonalisationTargeting
            salonId={salonId}
            value={pick(form, ['targetingType', 'targetingCriteria'])}
            onChange={(targeting) => updateForm(targeting)}
            errors={errors}
          />

          <PersonalisationScheduling
            idPrefix={activeSubTab}
            value={pick(form, SCHEDULE_KEYS)}
            onChange={(schedule) => updateForm(schedule)}
            errors={errors}
          />

          <div className="campaign-actions">
            <button type="button" className="cancel-button" onClick={handleReset} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="actual-blast-button" onClick={handleSave} disabled={saving}>
              {saving ? 'Scheduling...' : 'Schedule Personalisation'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PortalPersonalisationEditor;
